// Toppers site footer rendered in-tree by PageSkeleton.
//
// The footer navigation is serialized from the "footer" NavMenu in
// chrome._build_footer_nav_columns (mirroring how the header serializes
// top_nav_menu) and rendered here as a real React tree: the always-on contact
// bar (phone + Email Us) at the top, the desktop footer nav and the mobile
// accordion. The a360 badge, EmailSignupFooter island and footer-legal render
// below it.
//
// The mobile accordion blocks own their own open/close state: clicking a
// block header flips the block's active class. The header is a `<div>`, so it
// is exposed as a keyboard-operable `role="button"` with `aria-expanded`;
// blocks start closed.
import { Immutable } from "immer";
import React, { useState } from "react";
import { t } from "ttag";

import { Context } from "@reactivated";

import { EditorContent } from "@thelabnyc/thelabui/src/components/EditorContent";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { EmailSignupFooter } from "../EmailSignupFooter";

import styles from "./index.module.scss";

// Mutable reactivated props (not Immutable<>) to match the PageSkeleton chain.
// The footer now lives on GlobalChrome (the `chrome` context processor), so its
// type is derived from the reactivated Context rather than a page @template.
type IFooter = React.ContextType<typeof Context>["chrome"]["footer"];

// A single ordered footer-nav sub_nav item: a link block or a raw-HTML block
// (e.g. the OneTrust cookie-settings button), interleaved in stream order.
type IFooterNavCategory =
    IFooter["footer_nav_columns"][number]["categories"][number];
type IFooterNavItem = IFooterNavCategory["items"][number];

type IProps = {
    footer: IFooter;
};

// Desktop footer-nav sub_nav item. Every item carries the
// `w-block-{type} block-{type}` wrapper that Wagtail's `render_basic` emitted
// around each `{{ self.sub_nav }}` child. A raw block injects its server render
// (already `<div class="raw-html-block">...`) directly into that wrapper; a link
// block renders the item-block anchor structure.
const FooterNavDesktopItem = (
    props: Immutable<{ item: IFooterNavItem }>,
): React.JSX.Element => {
    const { item } = props;
    const wblockClass = `w-block-${item.block_type} block-${item.block_type}`;
    if (item.is_raw) {
        return (
            <EditorContent>
                {(marker) => (
                    <div
                        className={wblockClass}
                        dangerouslySetInnerHTML={{ __html: item.raw_html }}
                        {...marker}
                    />
                )}
            </EditorContent>
        );
    }
    return (
        <div className={wblockClass}>
            <div className={styles.footerNavItemBlock}>
                <div className={styles.footerNavItem}>
                    <a
                        className={concatClassNames([
                            styles.footerNavItemLink,
                            "footer-nav__item-link",
                        ])}
                        href={item.url}
                        target={item.open_in_new_tab ? "_blank" : undefined}
                        rel={
                            item.open_in_new_tab
                                ? "noopener noreferrer"
                                : undefined
                        }
                    >
                        {item.title}
                    </a>
                </div>
            </div>
        </div>
    );
};

// Mobile footer-nav sub_nav item. The old mobile markup was hand-built
// and UNWRAPPED (no `w-block-*`), so a raw block injects its server render
// directly. `display: contents` keeps the wrapper from adding a layout box, so
// the `raw-html-block` sits in the block dropdown exactly as the old
// `{{ sub_nav }}` render did.
const FooterNavMobileItem = (
    props: Immutable<{ item: IFooterNavItem }>,
): React.JSX.Element => {
    const { item } = props;
    if (item.is_raw) {
        return (
            <EditorContent>
                {(marker) => (
                    <div
                        style={{ display: "contents" }}
                        dangerouslySetInnerHTML={{ __html: item.raw_html }}
                        {...marker}
                    />
                )}
            </EditorContent>
        );
    }
    return (
        <div
            className={concatClassNames([
                styles.footerNavItem,
                styles.footerNavItemMobile,
            ])}
        >
            <a className={styles.footerNavItemMobileLink} href={item.url}>
                {item.title}
            </a>
        </div>
    );
};

// A mobile footer-nav block: the header toggles the block's active state,
// which the module uses to reveal the dropdown.
const FooterNavMobileBlock = (
    props: Immutable<{ category: IFooterNavCategory }>,
): React.JSX.Element => {
    const { category } = props;
    const [isOpen, setIsOpen] = useState(false);
    const toggle = () => {
        setIsOpen((open) => !open);
    };
    return (
        <div
            className={concatClassNames([
                styles.footerNavMobileBlock,
                isOpen ? styles.footerNavMobileBlockActive : null,
            ])}
        >
            <div
                className={concatClassNames([
                    styles.footerNavMobileHeader,
                    styles.siteFooterHeader,
                ])}
                role="button"
                tabIndex={0}
                aria-expanded={isOpen ? "true" : "false"}
                onClick={toggle}
                onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        toggle();
                    }
                }}
            >
                {category.title}
            </div>
            <div className={styles.siteFooterDropdown}>
                {category.items.map((item, itemIndex) => (
                    <FooterNavMobileItem key={itemIndex} item={item} />
                ))}
            </div>
        </div>
    );
};

export const SiteFooter = (props: IProps): React.JSX.Element => {
    const { footer } = props;
    // Evaluated at render scope so the locale is resolved per-request (toppers
    // serves fr_CA), not frozen at module load.
    const a360Alt = t`An Accessible360 badge sharing that we've partnered with an industry-leading ADA vendor who maintains our site is in good standing with accessibility.`;
    return (
        <footer
            className={concatClassNames([
                styles.siteFooter,
                "site-footer",
                "l-full-width",
            ])}
        >
            <div className={styles.siteFooterBorder} />

            {/*
             * The footer nav (contact bar + desktop nav + mobile
             * accordion) rendered from the serialized "footer"
             * NavMenu. The wrapper div preserves the previous chrome DOM (the
             * three sections were siblings inside a single container div).
             */}
            <div>
                <div className={styles.siteFooterContact}>
                    <div className="l-capped-width">
                        <a
                            className={styles.siteFooterContactItem}
                            href={`tel:${footer.footer_contact_phone}`}
                        >
                            <span
                                className={concatClassNames([
                                    styles.footerNavIcon,
                                    styles.footerNavPhoneIcon,
                                ])}
                            />{" "}
                            {footer.footer_contact_phone}
                        </a>
                        <a
                            href={footer.contact_us_url}
                            className={styles.siteFooterContactItem}
                        >
                            <span
                                className={concatClassNames([
                                    styles.footerNavIcon,
                                    styles.footerNavEmailIcon,
                                ])}
                            />
                            {t`Email Us`}
                        </a>
                    </div>
                </div>

                <nav
                    className={concatClassNames([
                        styles.siteFooterNavContainer,
                        "l-capped-width",
                    ])}
                >
                    <div className={styles.footerNav}>
                        {footer.footer_nav_columns.map(
                            (column, columnIndex) => (
                                <div
                                    key={columnIndex}
                                    className={styles.footerNavCol}
                                >
                                    <ul className={styles.footerNavList}>
                                        {column.categories.map(
                                            (category, categoryIndex) => (
                                                <React.Fragment
                                                    key={categoryIndex}
                                                >
                                                    <li
                                                        className={
                                                            styles.siteFooterHeader
                                                        }
                                                        role="heading"
                                                        aria-level={2}
                                                    >
                                                        {category.title}
                                                    </li>
                                                    {category.items.map(
                                                        (item, itemIndex) => (
                                                            <FooterNavDesktopItem
                                                                key={itemIndex}
                                                                item={item}
                                                            />
                                                        ),
                                                    )}
                                                </React.Fragment>
                                            ),
                                        )}
                                    </ul>
                                </div>
                            ),
                        )}
                    </div>
                </nav>

                <nav
                    className={concatClassNames([
                        styles.footerNavMobile,
                        "u-margin-bottom--xl",
                    ])}
                >
                    {footer.footer_nav_columns.map((column, columnIndex) =>
                        column.categories.map((category, categoryIndex) => (
                            <FooterNavMobileBlock
                                key={`${columnIndex}-${categoryIndex}`}
                                category={category}
                            />
                        )),
                    )}
                </nav>
            </div>

            <div
                className={concatClassNames([
                    styles.footerBadges,
                    "l-capped-width",
                ])}
            >
                <div className={styles.footerBadgesItemA360}>
                    <a
                        href="https://allyant.com/"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <img
                            className="responsive-img"
                            src={footer.a360_badge_url}
                            alt={a360Alt}
                            loading="lazy"
                            decoding="async"
                        />
                    </a>
                </div>
            </div>

            <EmailSignupFooter />

            <div
                className={concatClassNames([
                    styles.footerLegal,
                    "footer-legal",
                    "l-capped-width",
                ])}
            >
                <div className={styles.footerLegalCopyright}>
                    <ul className={styles.footerLegalList}>
                        <li className={styles.footerLegalItem}>
                            &copy; {footer.copyright_year} Tempur-Pedic North
                            America, LLC. {t`All Rights Reserved.`}
                        </li>
                        <br className={styles.footerLegalItemBr} />
                        <li className={styles.footerLegalItem}>
                            <span
                                className={concatClassNames([
                                    styles.footerLegalItemDivider,
                                    styles.footerLegalItemDividerHide,
                                ])}
                            >
                                |
                            </span>
                            <a href={footer.privacy_policy_url}>
                                {t`Privacy Policy`}
                            </a>
                        </li>
                        <li className={styles.footerLegalItem}>
                            <span className={styles.footerLegalItemDivider}>
                                |
                            </span>
                            <a href={footer.terms_url}>{t`Terms of Use`}</a>
                        </li>
                    </ul>
                </div>
                <div className={styles.footerLegalAuxCopy}>
                    {footer.footer_copy.map((item, i) => (
                        <div
                            key={i}
                            dangerouslySetInnerHTML={{ __html: item.html }}
                        />
                    ))}
                </div>
            </div>
        </footer>
    );
};
